/**
 * Inscription — wizard 3 étapes : compte → église → structure.
 * Crée le Reseau + l'Eglise + le User pasteur en une seule transaction backend.
 */
import { useState, useEffect } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { colors, fonts, goldGradient, TAGLINE } from "../styles/tokens";
import { authApi, tokenStorage } from "../services/api";
import useAuth from "../hooks/useAuth";
import useToast from "../hooks/useToast";
import Logo from "../components/Logo";
import Input from "../components/Input";
import Select from "../components/Select";
import Button from "../components/Button";
import WhatsAppFAB from "../components/WhatsAppFAB";

const PAYS = [
  "Côte d'Ivoire", "Sénégal", "Burkina Faso", "Mali", "Bénin", "Togo",
  "Cameroun", "Gabon", "Tchad", "Congo (RDC)", "Congo (Brazzaville)",
  "France", "Belgique", "Suisse", "Canada", "Haïti", "Madagascar", "Maurice", "Autre",
];

const DEVISES = [
  { value: "EUR", label: "Euro (€)" },
  { value: "XOF", label: "FCFA (XOF)" },
  { value: "XAF", label: "FCFA (XAF)" },
  { value: "CAD", label: "Dollar canadien (CAD)" },
  { value: "CHF", label: "Franc suisse (CHF)" },
  { value: "USD", label: "Dollar US (USD)" },
];

export default function Inscription() {
  const { isAuthenticated, updateUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [audience, setAudience] = useState(null); // null | "pasteur" | "fidele"
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    // Étape 1
    nom: "",
    email: "",
    password: "",
    password_confirm: "",
    // Étape 2
    eglise_nom: "",
    eglise_ville: "",
    eglise_pays: "Côte d'Ivoire",
    eglise_devise: "XOF",
    // Étape 3
    type_structure: "independante",
    reseau_nom: "",
    // Fidèle
    code_invitation: "",
  });

  // Pré-remplit l'email depuis ?email=… (envoyé par CtaFinal de la landing)
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setForm((p) => ({ ...p, email: emailParam }));
    }
  }, [searchParams]);

  if (isAuthenticated && !success) return <Navigate to="/dashboard" replace />;

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: null }));
    setGlobalError(null);
  };

  // ─── Validations par étape ───
  const validateStep1 = () => {
    const e = {};
    if (!form.nom.trim() || form.nom.trim().length < 2) e.nom = "Nom complet requis (min. 2 caractères)";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Email invalide";
    if (!form.password || form.password.length < 6) e.password = "Mot de passe trop court (min. 6 caractères)";
    if (form.password !== form.password_confirm) e.password_confirm = "Les mots de passe ne correspondent pas";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.eglise_nom.trim() || form.eglise_nom.trim().length < 2) e.eglise_nom = "Nom de l'église requis";
    if (!form.eglise_ville.trim()) e.eglise_ville = "Ville requise";
    if (!form.eglise_pays) e.eglise_pays = "Pays requis";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep3 = () => {
    const e = {};
    if (form.type_structure !== "independante" && !form.reseau_nom.trim()) {
      e.reseau_nom = "Nom du réseau requis";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateFidele = () => {
    const e = {};
    if (!form.nom.trim() || form.nom.trim().length < 2) e.nom = "Nom complet requis";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Email invalide";
    if (!form.password || form.password.length < 6) e.password = "Mot de passe trop court (min. 6 caractères)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ─── Soumission fidèle ───
  const handleSubmitFidele = async () => {
    if (!validateFidele()) return;
    setSubmitting(true);
    setGlobalError(null);
    try {
      const payload = {
        nom: form.nom.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        code_invitation: form.code_invitation.trim() ? form.code_invitation.trim().toUpperCase() : null,
      };
      const { data } = await authApi.registerFidele(payload);
      tokenStorage.set({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        user: data.user,
      });
      updateUser(data.user);
      toast.success(`Bienvenue ${data.user.full_name} ! Votre espace est prêt.`);
      navigate("/profil");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg = "Erreur lors de l'inscription";
      if (status === 409) msg = "Cet email est déjà utilisé";
      else if (status === 404) msg = "Code d'invitation invalide";
      else if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
      setGlobalError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Soumission finale ───
  const handleSubmit = async () => {
    if (!validateStep3()) return;
    setSubmitting(true);
    setGlobalError(null);
    try {
      const payload = {
        nom: form.nom.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        eglise_nom: form.eglise_nom.trim(),
        eglise_ville: form.eglise_ville.trim(),
        eglise_pays: form.eglise_pays,
        eglise_devise: form.eglise_devise,
        type_structure: form.type_structure,
        reseau_nom: form.type_structure === "independante" ? null : form.reseau_nom.trim(),
      };
      const { data } = await authApi.register(payload);

      // Stocke les tokens et l'user dans le contexte → connexion automatique
      tokenStorage.set({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        user: data.user,
      });
      updateUser(data.user);
      setSuccess(true);

      // Animation : on attend 1.2s pour montrer les ✓ avant de rediriger
      setTimeout(() => {
        toast.success(`Bienvenue ${data.user.full_name} ! Votre église est prête.`);
        navigate("/dashboard");
      }, 1200);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let msg = "Erreur lors de l'inscription";
      if (status === 409) msg = "Cet email est déjà utilisé. Connectez-vous ou utilisez un autre email.";
      else if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
      setGlobalError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ───
  return (
    <div
      style={{
        minHeight: "100vh",
        background: `radial-gradient(circle at 15% 0%, rgba(228,167,44,0.10) 0%, transparent 50%), ${colors.bg}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        fontFamily: fonts.body,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: "20px",
          padding: "36px 32px",
          boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
        }}
      >
        <Link to="/" style={{ textDecoration: "none", display: "block", marginBottom: "20px" }}>
          <Logo size="md" showName />
        </Link>

        {/* Stepper visible uniquement pour le flux pasteur */}
        {audience === "pasteur" && !success && <Stepper step={step} />}

        {success ? (
          <SuccessPanel form={form} />
        ) : audience === null ? (
          <ChooserScreen onPick={setAudience} />
        ) : (
          <div className="fade-in">
            {globalError && (
              <div style={{
                background: "rgba(224,91,106,0.1)",
                border: `1px solid ${colors.rose}`,
                color: colors.rose,
                padding: "10px 14px",
                borderRadius: "10px",
                fontSize: "13px",
                marginBottom: "16px",
              }}>
                {globalError}
              </div>
            )}

            {audience === "pasteur" && step === 1 && (
              <Step1
                form={form} set={set} errors={errors}
                onPrev={() => setAudience(null)}
                onNext={() => validateStep1() && setStep(2)}
              />
            )}
            {audience === "pasteur" && step === 2 && (
              <Step2
                form={form} set={set} errors={errors}
                onPrev={() => setStep(1)}
                onNext={() => validateStep2() && setStep(3)}
              />
            )}
            {audience === "pasteur" && step === 3 && (
              <Step3
                form={form} set={set} errors={errors}
                submitting={submitting}
                onPrev={() => setStep(2)}
                onSubmit={handleSubmit}
              />
            )}
            {audience === "fidele" && (
              <FideleStep
                form={form} set={set} errors={errors}
                submitting={submitting}
                onPrev={() => setAudience(null)}
                onSubmit={handleSubmitFidele}
              />
            )}

            <p style={{ marginTop: "24px", textAlign: "center", fontSize: "13px", color: colors.textMuted }}>
              Déjà un compte ? <Link to="/login" style={{ color: colors.gold, fontWeight: 600 }}>Se connecter</Link>
            </p>
          </div>
        )}
      </div>
      <WhatsAppFAB />
    </div>
  );
}

// ─── Étapes ──────────────────────────────────────────────────────────────

function Step1({ form, set, errors, onPrev, onNext }) {
  return (
    <>
      <h2 style={{ fontFamily: fonts.heading, fontSize: "26px", fontWeight: 700, marginBottom: "4px" }}>
        Créez votre compte
      </h2>
      <p style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "20px" }}>
        Gratuit · Sans carte bancaire · Mise en route en 2 minutes
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <Input label="Nom complet" value={form.nom} onChange={set("nom")} placeholder="Pasteur Jean Dupont" required error={errors.nom} />
        <Input label="Email" type="email" value={form.email} onChange={set("email")} placeholder="vous@exemple.com" required error={errors.email} icon="✉" autoComplete="email" />
        <Input label="Mot de passe" type="password" value={form.password} onChange={set("password")} placeholder="Au moins 6 caractères" required error={errors.password} icon="🔒" autoComplete="new-password" />
        <Input label="Confirmer le mot de passe" type="password" value={form.password_confirm} onChange={set("password_confirm")} required error={errors.password_confirm} icon="🔒" autoComplete="new-password" />
        <div style={{ display: "flex", gap: "10px" }}>
          {onPrev && <Button variant="ghost" onClick={onPrev}>← Retour</Button>}
          <Button onClick={onNext} fullWidth size="lg">Continuer →</Button>
        </div>
      </div>
    </>
  );
}

function ChooserScreen({ onPick }) {
  return (
    <div className="fade-in" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <h2 style={{ fontFamily: fonts.heading, fontSize: 24, fontWeight: 700, textAlign: "center", marginBottom: 4 }}>
        Vous êtes... ?
      </h2>
      <p style={{ color: colors.textMuted, fontSize: 13, textAlign: "center", marginBottom: 8 }}>
        Choisissez le profil qui vous correspond
      </p>
      <AudienceCard
        icon="⛪"
        title="Pasteur / Responsable d'église"
        desc="Je veux gérer mon église, mes membres et mes finances"
        accent={colors.gold}
        onClick={() => onPick("pasteur")}
      />
      <AudienceCard
        icon="🙏"
        title="Fidèle / Membre"
        desc="Mon pasteur m'a invité(e) à rejoindre l'application"
        accent={colors.blue}
        onClick={() => onPick("fidele")}
      />
    </div>
  );
}

function AudienceCard({ icon, title, desc, accent, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "18px 20px",
        background: hover ? `${accent}18` : colors.card,
        border: `1.5px solid ${hover ? accent : colors.border}`,
        borderRadius: 14,
        textAlign: "left",
        cursor: "pointer",
        transition: "all 200ms",
        transform: hover ? "translateY(-1px)" : "none",
      }}
    >
      <span style={{ fontSize: 36, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: colors.text, fontSize: 15, fontWeight: 700, marginBottom: 2 }}>{title}</div>
        <div style={{ color: colors.textMuted, fontSize: 12, lineHeight: 1.4 }}>{desc}</div>
      </div>
      <span style={{ color: hover ? accent : colors.textMuted, fontSize: 20 }}>→</span>
    </button>
  );
}

function FideleStep({ form, set, errors, submitting, onPrev, onSubmit }) {
  return (
    <>
      <h2 style={{ fontFamily: fonts.heading, fontSize: "26px", fontWeight: 700, marginBottom: "4px" }}>
        Rejoindre une église
      </h2>
      <p style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "20px" }}>
        🙏 Espace fidèle · Vos contributions, présences et événements en un clic.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <Input label="Nom complet" value={form.nom} onChange={set("nom")} placeholder="Jean-Baptiste Kouassi" required error={errors.nom} />
        <Input label="Email" type="email" value={form.email} onChange={set("email")} placeholder="vous@exemple.com" required error={errors.email} icon="✉" autoComplete="email" />
        <Input label="Mot de passe" type="password" value={form.password} onChange={set("password")} placeholder="Au moins 6 caractères" required error={errors.password} icon="🔒" autoComplete="new-password" />
        <Input
          label="Code d'invitation (optionnel)"
          value={form.code_invitation}
          onChange={set("code_invitation")}
          placeholder="ABCD1234"
          icon="🔑"
        />
        <p style={{ color: colors.textMuted, fontSize: 12, marginTop: -6, marginLeft: 4 }}>
          Fourni par votre pasteur — sinon, votre compte sera créé sans église.
        </p>
        <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
          <Button variant="ghost" onClick={onPrev} disabled={submitting}>← Retour</Button>
          <Button onClick={onSubmit} fullWidth size="lg" loading={submitting} disabled={submitting}>
            {submitting ? "Création en cours…" : "Créer mon compte fidèle →"}
          </Button>
        </div>
      </div>
    </>
  );
}

function Step2({ form, set, errors, onPrev, onNext }) {
  return (
    <>
      <h2 style={{ fontFamily: fonts.heading, fontSize: "26px", fontWeight: 700, marginBottom: "4px" }}>
        Parlez-nous de votre église
      </h2>
      <p style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "20px" }}>
        Elle sera créée automatiquement et prête à l'emploi.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <Input label="Nom de l'église" value={form.eglise_nom} onChange={set("eglise_nom")} placeholder="Ex : Église de la Grâce" required error={errors.eglise_nom} icon="⛪" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <Input label="Ville" value={form.eglise_ville} onChange={set("eglise_ville")} placeholder="Abidjan" required error={errors.eglise_ville} />
          <Select label="Pays" value={form.eglise_pays} onChange={set("eglise_pays")} options={PAYS.map((p) => ({ value: p, label: p }))} required placeholder={null} />
        </div>
        <Select label="Devise principale" value={form.eglise_devise} onChange={set("eglise_devise")} options={DEVISES} placeholder={null} />
        <div style={{ display: "flex", gap: "10px" }}>
          <Button variant="ghost" onClick={onPrev}>← Retour</Button>
          <Button onClick={onNext} fullWidth>Continuer →</Button>
        </div>
      </div>
    </>
  );
}

function Step3({ form, set, errors, submitting, onPrev, onSubmit }) {
  const choices = [
    { id: "independante", title: "Église indépendante", desc: "Je dirige une seule église, sans réseau" },
    { id: "membre_reseau", title: "Membre d'un réseau", desc: "Mon église fait partie d'un réseau plus large" },
    { id: "tete_reseau", title: "Tête de réseau", desc: "Je dirige un réseau multi-églises" },
  ];

  return (
    <>
      <h2 style={{ fontFamily: fonts.heading, fontSize: "26px", fontWeight: 700, marginBottom: "4px" }}>
        Votre structure
      </h2>
      <p style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "20px" }}>
        Cela nous aide à adapter Mon Ministère à votre organisation.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {choices.map((c) => {
          const active = form.type_structure === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => set("type_structure")(c.id)}
              style={{
                padding: "14px 16px",
                borderRadius: "10px",
                border: `1px solid ${active ? colors.gold : colors.border}`,
                background: active ? colors.goldDim : colors.bg,
                textAlign: "left",
                cursor: "pointer",
                transition: "all 150ms",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{
                  width: 18, height: 18, borderRadius: "50%",
                  border: `2px solid ${active ? colors.gold : colors.textMuted}`,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {active && <span style={{ width: 10, height: 10, borderRadius: "50%", background: colors.gold }} />}
                </span>
                <div>
                  <div style={{ color: colors.text, fontWeight: 600, fontSize: "14px" }}>{c.title}</div>
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginTop: "2px" }}>{c.desc}</div>
                </div>
              </div>
            </button>
          );
        })}

        {form.type_structure !== "independante" && (
          <Input
            label="Nom du réseau"
            value={form.reseau_nom}
            onChange={set("reseau_nom")}
            placeholder="Ex : Réseau Grâce International"
            required
            error={errors.reseau_nom}
            style={{ marginTop: "8px" }}
          />
        )}

        <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
          <Button variant="ghost" onClick={onPrev} disabled={submitting}>← Retour</Button>
          <Button onClick={onSubmit} fullWidth size="lg" loading={submitting} disabled={submitting}>
            {submitting ? "Création en cours…" : "Créer mon compte →"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ─── Stepper visuel ───
function Stepper({ step }) {
  const labels = ["Compte", "Église", "Structure"];
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "24px" }}>
      {labels.map((label, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: active ? goldGradient : (done ? colors.gold : colors.surface),
              color: active || done ? colors.bg : colors.textMuted,
              fontSize: "13px", fontWeight: 700,
              border: `1px solid ${active || done ? colors.gold : colors.border}`,
            }}>
              {done ? "✓" : idx}
            </div>
            <span style={{ fontSize: "13px", color: active ? colors.gold : colors.textMuted, fontWeight: active ? 600 : 500 }}>
              {label}
            </span>
            {i < labels.length - 1 && <span style={{ color: colors.border }}>—</span>}
          </div>
        );
      })}
    </div>
  );
}

// ─── Panneau de succès ───
function SuccessPanel({ form }) {
  return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div style={{
        width: 64, height: 64, borderRadius: "50%",
        background: colors.green, color: colors.bg,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: "36px", fontWeight: 700,
        marginBottom: "16px",
        animation: "pulse-gold 1.2s ease-in-out",
      }}>
        ✓
      </div>
      <h2 style={{ fontFamily: fonts.heading, fontSize: "26px", fontWeight: 700, marginBottom: "10px" }}>
        Bienvenue !
      </h2>
      <div style={{ color: colors.text, fontSize: "14px", display: "flex", flexDirection: "column", gap: "6px", margin: "20px 0" }}>
        <div>✓ Compte créé</div>
        <div>✓ Église <strong style={{ color: colors.gold }}>« {form.eglise_nom} »</strong> créée</div>
        <div>✓ Connexion automatique</div>
      </div>
      <p style={{ color: colors.textMuted, fontSize: "13px" }}>Redirection vers votre tableau de bord…</p>
    </div>
  );
}
